import React, { useMemo, useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth'
import { Building2, Crown, Sparkles } from 'lucide-react'
import { auth, db } from '../firebase'
import { doc, setDoc } from '../security/SecureFirebase.js'
import TenantService from '../services/firebase/tenants'
import stripeConnectService from '../services/StripeConnectService'
import { uploadTenantLogo } from '../services/firebase/storage'

const PLAN_CONFIG = {
  basic: {
    title: 'Welcome to Market Genie!',
    subtitle: 'Your starter tools are ready. Create your account and begin building your business.',
    accent: 'from-sky-500 to-cyan-400',
    panel: 'border-sky-200',
    icon: Sparkles,
    button: 'from-sky-500 to-cyan-400 hover:from-sky-600 hover:to-cyan-500',
    hasWhiteLabel: false,
    label: 'Starter Plan'
  },
  pro: {
    title: 'Welcome to Market Genie!',
    subtitle: 'Your professional tools are now unlocked. Let’s finish your setup.',
    accent: 'from-blue-600 to-cyan-500',
    panel: 'border-blue-200',
    icon: Sparkles,
    button: 'from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600',
    hasWhiteLabel: false,
    label: 'Growth Plan'
  },
  enterprise: {
    title: 'Welcome to Market Genie!',
    subtitle: 'Your premium business tools are ready. Complete your account to start scaling.',
    accent: 'from-indigo-700 to-violet-500',
    panel: 'border-violet-200',
    icon: Building2,
    button: 'from-indigo-700 to-violet-500 hover:from-indigo-800 hover:to-violet-600',
    hasWhiteLabel: false,
    label: 'Scale Plan'
  },
  pro_monthly: {
    title: 'Welcome to the Genie!',
    subtitle: 'Your professional tools are now unlocked.',
    accent: 'from-blue-600 to-cyan-500',
    panel: 'border-blue-200',
    icon: Sparkles,
    button: 'from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600',
    hasWhiteLabel: false,
    label: 'Pro Monthly'
  },
  pro_yearly: {
    title: 'Welcome to the Genie!',
    subtitle: 'Your professional tools are now unlocked.',
    accent: 'from-blue-700 to-indigo-500',
    panel: 'border-blue-200',
    icon: Sparkles,
    button: 'from-blue-700 to-indigo-500 hover:from-blue-800 hover:to-indigo-600',
    hasWhiteLabel: false,
    label: 'Pro Yearly'
  },
  whitelabel_yearly: {
    title: "Welcome, Platform Partner! Let's set up your brand.",
    subtitle: 'Your white-label toolkit is ready. Claim your account to start customizing it.',
    accent: 'from-amber-500 to-yellow-400',
    panel: 'border-amber-200',
    icon: Crown,
    button: 'from-amber-500 to-yellow-400 hover:from-amber-600 hover:to-yellow-500',
    hasWhiteLabel: true,
    label: 'White Label Yearly'
  }
}

export default function ClaimAccount() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    companyName: ''
  })
  const [logoFile, setLogoFile] = useState(null)
  const [logoPreview, setLogoPreview] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [stripeMetadata, setStripeMetadata] = useState(null)

  const success = searchParams.get('success') === 'true'
  const plan = (searchParams.get('plan') || '').toLowerCase()
  const sessionId = searchParams.get('session_id') || ''
  const partnerCode = searchParams.get('partnerCode') || stripeMetadata?.metadata?.partnerCode || ''
  const referralCampaign = searchParams.get('campaign') || stripeMetadata?.metadata?.campaign || ''
  const referralSource = searchParams.get('source') || stripeMetadata?.metadata?.source || ''
  const referralDiscount = parseInt(searchParams.get('discount')) || parseInt(stripeMetadata?.metadata?.discount) || 0
  const amountPaidParam = searchParams.get('amountPaid')
  const parsedAmountPaid = amountPaidParam !== null ? Number(amountPaidParam) : NaN
  const amountPaidCents = Number.isFinite(parsedAmountPaid)
    ? parsedAmountPaid
    : stripeMetadata?.amountTotal ?? undefined
  const amountPaid = amountPaidCents > 0 ? amountPaidCents / 100 : undefined
  const config = useMemo(() => PLAN_CONFIG[plan], [plan])
  const planLabel = config?.label || (plan === 'basic' ? 'Starter Plan' : plan === 'pro' ? 'Growth Plan' : plan === 'enterprise' ? 'Scale Plan' : 'Professional')
  const resolvedPlanName = config?.hasWhiteLabel ? 'Agency' : planLabel
  const resolvedStatus = config?.hasWhiteLabel ? 'Active' : 'active'

  useEffect(() => {
    const fetchStripeMetadata = async () => {
      if (!sessionId || stripeMetadata) return
      try {
        const data = await stripeConnectService.getStripeCheckoutSessionMetadata(sessionId)
        setStripeMetadata(data)
      } catch (error) {
        console.warn('Unable to fetch Stripe checkout session metadata:', error)
      }
    }

    fetchStripeMetadata()
  }, [sessionId, stripeMetadata])

  const handleInputChange = (event) => {
    const { name, value } = event.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleLogoChange = (event) => {
    const file = event.target.files?.[0]
    setLogoFile(file || null)

    if (!file) {
      setLogoPreview('')
      return
    }

    const reader = new FileReader()
    reader.onload = (loadEvent) => {
      setLogoPreview(loadEvent.target?.result || '')
    }
    reader.readAsDataURL(file)
  }

  const handleCreateAccount = async (event) => {
    event.preventDefault()
    setError('')
    setIsSubmitting(true)

    try {
      const credential = await createUserWithEmailAndPassword(auth, formData.email, formData.password)
      const createdUser = credential.user

      await updateProfile(createdUser, {
        displayName: formData.companyName
      })

      await createdUser.getIdToken(true)

      const shouldHaveWhiteLabel = config?.hasWhiteLabel || plan === 'enterprise'
      const tenantResult = await TenantService.createTenantForUser(auth.currentUser || createdUser, resolvedPlanName, {
        planType: plan,
        planName: planLabel,
        billingCycle: plan === 'pro_yearly' || plan === 'whitelabel_yearly' ? 'yearly' : 'monthly',
        hasWhiteLabel: shouldHaveWhiteLabel,
        businessName: formData.companyName,
        status: resolvedStatus,
        partnerCode: partnerCode || null,
        referralCampaign: referralCampaign || null,
        referralSource: referralSource || null,
        referralDiscount: referralDiscount || 0,
        amountPaid: amountPaid
      })
      if (tenantResult.error) {
        throw new Error(tenantResult.error.message || 'Failed to create tenant workspace')
      }

      const existingSettings = tenantResult.data?.settings || {}
      const billingAmount = amountPaid != null
        ? amountPaid
        : plan === 'basic'
          ? 47
          : plan === 'pro' || plan === 'pro_monthly' || plan === 'professional'
            ? 29
            : plan === 'enterprise' || plan === 'scale' || plan === 'scale plan'
              ? 197
              : plan === 'pro_yearly'
                ? 261
                : plan === 'whitelabel_yearly' || plan === 'agency'
                  ? 497
                  : plan === 'lifetime'
                    ? 300
                    : 0

      await setDoc(doc(db, 'MarketGenie_tenants', createdUser.uid), {
        name: `${formData.companyName}'s Workspace`,
        plan: resolvedPlanName,
        planType: plan,
        status: resolvedStatus,
        hasWhiteLabel: shouldHaveWhiteLabel,
        whiteLabelActivatedAt: shouldHaveWhiteLabel ? new Date() : null,
        subscriptionStatus: 'active',
        billing: {
          plan: resolvedPlanName,
          status: 'active',
          cycle: plan === 'pro_yearly' || plan === 'whitelabel_yearly' ? 'yearly' : 'monthly',
          amount: billingAmount,
          lifetime: plan === 'lifetime'
        },
        settings: {
          ...existingSettings,
          companyName: formData.companyName,
          logoUrl: existingSettings.logoUrl || null
        }
      }, { merge: true })

      if (config?.hasWhiteLabel && logoFile) {
        const logoResult = await uploadTenantLogo(createdUser.uid, logoFile)
        if (!logoResult.success) {
          console.warn('Logo upload warning:', logoResult.error)
        }
      }

      navigate('/dashboard', { replace: true })
    } catch (err) {
      console.error('Claim account error:', err)
      setError(err.message || 'Failed to create account. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!success || !config) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center border border-slate-200">
          <Building2 className="h-14 w-14 mx-auto text-slate-500 mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Invalid Claim Link</h1>
          <p className="text-slate-600 mb-6">
            This payment confirmation link is missing required details. Please contact support if you just completed a purchase.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="bg-slate-900 text-white px-5 py-3 rounded-lg font-semibold hover:bg-slate-800 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    )
  }

  const Icon = config.icon

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-4">
      <div className={`w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden border ${config.panel}`}>
        <div className={`bg-gradient-to-r ${config.accent} text-white p-6`}>
          <div className="flex items-center gap-3 mb-2">
            <Icon className="h-8 w-8" />
            <span className="text-sm font-semibold uppercase tracking-[0.2em]">{config.label}</span>
          </div>
          <h1 className="text-3xl font-bold mb-2">{config.title}</h1>
          <p className="text-sm md:text-base text-white/90">{config.subtitle}</p>
        </div>

        <div className="p-6 md:p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Create Your Account</h2>
            <p className="text-slate-600">
              Enter your details below to activate your workspace and jump straight into your new dashboard.
            </p>
          </div>

          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleCreateAccount} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="you@company.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
              <input
                type="password"
                name="password"
                required
                minLength={6}
                value={formData.password}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Create a secure password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Company Name</label>
              <input
                type="text"
                name="companyName"
                required
                value={formData.companyName}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Your Company Name"
              />
            </div>

            {config.hasWhiteLabel && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <label className="block text-sm font-medium text-amber-900 mb-2">Brand Logo (optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="w-full rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm"
                />
                <p className="text-xs text-amber-800 mt-2">Upload your logo now and it will be attached to your new white-label workspace.</p>

                {logoPreview && (
                  <div className="mt-4 rounded-lg bg-white border border-amber-200 p-3 inline-block">
                    <img src={logoPreview} alt="Logo preview" className="max-h-16 w-auto object-contain" />
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full bg-gradient-to-r ${config.button} text-white py-3 rounded-lg font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed`}
            >
              {isSubmitting ? 'Creating Account...' : 'Create Account & Open Dashboard'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
